import React, { useCallback, useState } from "react";
import { Alert, Breadcrumb } from "react-bootstrap";
import toast from "react-hot-toast";
import { Link, useParams } from "react-router-dom";
import LoadingMask from "../../components/LoadingMask/LoadingMask";
import PublicListingOverview from "../../components/PublicListingOverview/PublicListingOverview";
import { useDocumentTitle } from "../../hooks/use-document-title";
import { useProject } from "../../hooks/use-project";
import { usePublicListing } from "../../hooks/use-public-listing";
import { createPublicListing } from "../../services/publicListingService";
import ManageView from "./ManageView";
import StepBusinessInfo from "./StepBusinessInfo";
import StepClaimHandle from "./StepClaimHandle";
import StepDisplayOptions from "./StepDisplayOptions";
import StepWelcome from "./StepWelcome";
import { defaultFormData, WizardFormData } from "./types";
import "./PublicListingPage.scss";

const PublicListingPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const { project, isLoading: projectLoading } = useProject(projectId ?? null);
  const { publicListing, isLoading: listingLoading } = usePublicListing(
    projectId ?? null,
  );
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState<WizardFormData>(defaultFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useDocumentTitle(
    project ? `Public Listing - ${project.name}` : "Public Listing",
  );

  const handleChange = useCallback((updates: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleCreate = useCallback(async () => {
    if (!projectId) return;

    setIsSubmitting(true);
    try {
      await createPublicListing(projectId, {
        projectId,
        handle: formData.handle,
        status: "in-preview",
        companyName: formData.companyName || project?.name,
        revenueDisplay: formData.revenueDisplay,
        subscriptionsDisplay: formData.subscriptionsDisplay,
        countryDisplay: formData.countryDisplay,
        websiteUrl: formData.websiteUrl,
        contactEmail: formData.contactEmail || undefined,
        logoUrl: formData.logoUrl,
      });
      toast.success("Public listing created");
    } catch {
      toast.error("Failed to create listing");
    } finally {
      setIsSubmitting(false);
    }
  }, [projectId, formData, project?.name]);

  if (projectLoading || listingLoading) {
    return <LoadingMask message="Loading..." />;
  }

  if (!project) {
    return <Alert variant="danger">Project not found.</Alert>;
  }

  return (
    <div className="public-listing-page">
      <Breadcrumb>
        <Breadcrumb.Item
          linkAs={Link}
          linkProps={{ to: `/project/${projectId}` }}
        >
          {project.name}
        </Breadcrumb.Item>
        <Breadcrumb.Item active>Public Listing</Breadcrumb.Item>
      </Breadcrumb>

      <div className="page-header">Public Listing</div>

      {publicListing ? (
        <div className="row">
          <div className="col-lg-8">
            <ManageView listing={publicListing} projectId={projectId!} />
          </div>
          <div className="col-lg-4">
            <PublicListingOverview
              projectId={projectId!}
              publicListing={publicListing}
            />
          </div>
        </div>
      ) : (
        <>
          <div className="step-indicator">Step {step} of 4</div>

          {step === 1 && <StepWelcome onNext={() => setStep(2)} />}
          {step === 2 && (
            <StepDisplayOptions
              formData={formData}
              onChange={handleChange}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <StepBusinessInfo
              formData={formData}
              onChange={handleChange}
              onBack={() => setStep(2)}
              onNext={() => setStep(4)}
            />
          )}
          {step === 4 && (
            <StepClaimHandle
              formData={formData}
              onChange={handleChange}
              onBack={() => setStep(3)}
              onSubmit={handleCreate}
              isSubmitting={isSubmitting}
            />
          )}
        </>
      )}
    </div>
  );
};

export default PublicListingPage;
